<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { Editor, EditorContent, generateJSON } from "@tiptap/vue-3";
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import type { JSONContent } from "@tiptap/core";
import {
  Popover,
  PopoverAction,
  Popup,
  showLoadingToast,
  showNotify,
  showSuccessToast,
} from "vant";
import { useRoute, useRouter } from "vue-router";
import { useRenderExtension, useStory } from "@/stores/story";
import { timeAgo } from "@/modules/common";
import editorUtils from "@/modules/editor/editor-utils";
import GlobalScrollLayout from "@/components/mobile/layout/GlobalScrollLayout.vue";
import AddImageAction from "@/components/mobile/story/editor-action/AddImageAction.vue";
import AddParagraph from "@/components/mobile/story/editor-action/AddParagraph.vue";
import AiWritingPopup, {
  AiWritingMode,
  AiWritingPopupExpose,
  WriteByMomentParam,
} from "@/components/mobile/story/editor-action/AiWritingPopup.vue";
import MomentSelection, {
  MomentSelectionInstance,
} from "@/components/mobile/popup/MomentSelection.vue";
import UndoIcon from "@/components/icon/UndoIcon.vue";
import ToggleParagraphIndent from "@/components/mobile/story/editor-action/ToggleParagraphIndent.vue";
import { useAutoSave } from "@/components/mobile/story/editor-action/useAutoSave";
import i18n from "@/locales";
import { Story } from "@/types/entity";

// Types
interface AiWritingModeAction extends PopoverAction {
  text: string;
  key: AiWritingMode;
}

// Constants
const actions = [
  {
    text: i18n.global.t("story.g.basedPrev"),
    key: "prev",
  },
  {
    text: i18n.global.t("story.g.basedNext"),
    key: "next",
  },
  {
    text: i18n.global.t("story.g.basedContext"),
    key: "context",
  },
  {
    text: i18n.global.t("story.g.generatedFromMoment"),
    key: "moment",
  },
] as AiWritingModeAction[];
// State
const RENDER_EXTENSION = useRenderExtension();
const route = useRoute();
const router = useRouter();
const storyStore = useStory();
const currentStory = ref<Story | null>(null);
const lastUpdateMs = ref(0);
const lastUpdateTimeAgo = computed(() => {
  if (!currentStory.value?.dtUpdate) return null;
  return timeAgo(currentStory.value?.dtUpdate);
});
const loading = ref(false);
const content = ref<JSONContent>(generateJSON("", RENDER_EXTENSION)); // 编辑器内容（JSON）
const editorInstance = editorUtils.useStoryEditor({
  content: content.value,
  onUpdate: ({ editor }) => {
    (editor as Editor).view.dispatch(editor.view.state.tr.scrollIntoView());
    content.value = (editor as Editor).getJSON();
  },
});
// AI写作
const aiWritingRef = ref<AiWritingPopupExpose | null>(null);
const aiWritingPopupShow = ref(false);
// AI写作-模式选择
const aiWritingMode = ref<Exclude<AiWritingMode, "moment">>("context");
const showAiWritingModSelectPopup = ref(false);
const aiWritingModeText = computed(() => {
  return actions.find((item) => item.key === aiWritingMode.value)?.text;
});
const isSaving = ref(false);
// Lifecycle
const saveSilently = async () => {
  try {
    if (isSaving.value || !content.value) {
      return;
    }
    isSaving.value = true;
    const jsonContent = editorInstance.value.getJSON();
    editorUtils.removeAiTextNodeFromJson(jsonContent);
    currentStory.value = await storyStore.updateStory(currentStory.value!.id!, {
      text: JSON.stringify(jsonContent),
    });
  } finally {
    isSaving.value = false;
  }
};
const prepareData = async () => {
  loading.value = true;
  try {
    const uuid = route.params.uuid as string;
    currentStory.value = await storyStore.getStoryItem(uuid);
    content.value = storyStore.getJson(currentStory.value?.text ?? "");
    editorInstance.value
      .chain()
      .setMeta("addToHistory", false)
      .setContent(content.value)
      .run();
    lastUpdateMs.value = new Date(currentStory.value!.dtUpdate!).getTime();
  } finally {
    loading.value = false;
  }
};
const isAutoSaving = ref(false);
const autoSave = async () => {
  if (isAutoSaving.value) return;
  try {
    isAutoSaving.value = true;
    await saveSilently();
  } catch (e) {
    showNotify({
      type: "danger",
      message: i18n.global.t("Message.autoSaveFailed"),
    });
  } finally {
    isAutoSaving.value = false;
  }
};
const { status: autoSaveStatus } = useAutoSave(content, autoSave);
onMounted(async () => {
  await prepareData();
});
onBeforeUnmount(() => {
  if (currentStory.value) {
    saveSilently();
  }
});
// Event Handler
const onAiWritingClick = async (param?: WriteByMomentParam) => {
  aiWritingPopupShow.value = true;
  await nextTick(() => editorUtils.scrollToFrom(editorInstance.value));
  await aiWritingRef.value?.startAiWriting(param);
};
const onSaveClick = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.storySaving"),
    duration: 0,
  });
  try {
    isSaving.value = true;
    const jsonContent = editorInstance.value.getJSON();
    editorUtils.removeAiTextNodeFromJson(jsonContent);
    currentStory.value = await storyStore.updateStory(currentStory.value!.id!, {
      text: JSON.stringify(jsonContent),
    });
    lastUpdateMs.value = new Date().getTime();
    loadingToast.close();
    showSuccessToast(i18n.global.t("Message.saveSuccess"));
  } finally {
    isSaving.value = false;
    loadingToast.close();
  }
};

const onImgModeClick = async () => {
  await onSaveClick();
  router.replace({
    name: "storyImgModule",
    query: { story: currentStory.value!.uuid },
  });
};

const showMomentSelector = ref(false);
const momentSelectorRef = ref<MomentSelectionInstance | null>(null);
const selectedMoments = ref<Record<number, Moment>>({});
const onModeSelect = (action: AiWritingModeAction) => {
  if (action.key === "moment") {
    showMomentSelector.value = true;
    nextTick(() => {
      momentSelectorRef.value?.reset();
    });
  } else {
    aiWritingMode.value = action.key as Exclude<AiWritingMode, "moment">;
  }
};
const onWriteByMoment = () => {
  showMomentSelector.value = false;
  const selectedMoment = Object.values(selectedMoments.value)[0]!;
  const momentText = selectedMoment.text;
  const momentId = selectedMoment.id!;
  onAiWritingClick({ momentId, momentText });
};
</script>
<template>
  <global-scroll-layout>
    <template #nav>
      <div
        class="flex justify-between items-center h-[var(--top-nav-height)] gap-1 pr-2 bg-base-100"
      >
        <div v-if="currentStory" class="flex flex-1 items-center">
          <div>
            <button class="btn btn-ghost" @click="$router.back()">
              <i class="iconfont icon-left" />
            </button>
          </div>
          <div class="flex flex-col justify-center gap-1">
            <div class="font-bold line-clamp-1">{{ currentStory?.title }}</div>
            <div class="flex gap-2">
              <div
                class="badge badge-neutral badge-xs text-xs p-2 flex gap-1 items-center"
              >
                <div
                  :class="{
                    'bg-success': autoSaveStatus === 'done',
                    'bg-warning': autoSaveStatus !== 'done',
                  }"
                  class="rounded-full w-2 h-2"
                ></div>
                {{ lastUpdateTimeAgo }}
              </div>
              <div class="badge badge-neutral badge-xs text-xs p-2">
                {{
                  $t("story.totalWords", {
                    number: editorInstance.storage.characterCount.characters(),
                  })
                }}
              </div>
            </div>
          </div>
        </div>

        <div v-else>{{ $t("Message.loadingStory") }}</div>
        <!-- <div v-if="currentStory" class="flex items-center gap-1"> -->
        <div v-if="currentStory" class="flex items-center gap-1">
          <!-- <button class="btn btn-primary btn-sm rounded" @click="onImgModeClick">
            {{ $t("story.insertImgMode") }}
          </button> -->
          <button class="btn btn-primary btn-sm rounded" @click="onSaveClick">
            {{ $t("Buttons.save") }}
          </button>
        </div>
      </div>
    </template>
    <template #default>
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center"
      >
        <span class="loading"></span>
      </div>
      <div v-else-if="editorInstance" class="pt-12 md:pt-0 pb-[55%]">
        <div
          class="fixed md:sticky z-10 top-[var(--top-nav-height)] right-0 left-0 h-12 flex justify-between items-center px-2 border"
        >
          <div class="flex items-center">
            <button
              :disabled="aiWritingPopupShow"
              class="btn btn-xs md:btn-sm btn-ghost rounded"
              @click="editorInstance.commands.undo()"
            >
              <undo-icon class="icon-sm" />
            </button>
            <button
              :disabled="aiWritingPopupShow"
              class="btn btn-xs md:btn-sm btn-ghost rounded"
              @click="editorInstance.commands.redo()"
            >
              <undo-icon class="icon-sm transform scale-x-[-1]" />
            </button>
            <add-paragraph
              :disabled="aiWritingPopupShow"
              :editor="editorInstance"
            />
            <add-image-action
              :disabled="aiWritingPopupShow"
              :editor="editorInstance"
              :story-id="currentStory!.id!"
            />
            <toggle-paragraph-indent
              :disabled="aiWritingPopupShow"
              :editor="editorInstance"
            />
          </div>
          <div v-if="false" class="flex items-center gap-2">
            <popover
              v-model:show="showAiWritingModSelectPopup"
              :actions="actions"
              @select="onModeSelect"
            >
              <template #reference>
                <button
                  :disabled="aiWritingPopupShow"
                  class="btn btn-xs rounded"
                >
                  {{ aiWritingModeText }}
                </button>
              </template>
            </popover>
            <button
              :disabled="aiWritingPopupShow"
              class="btn btn-xs btn-primary rounded"
              @click="onAiWritingClick()"
            >
              <magic-stick-icon class="icon-sm" />
              {{ $t("Buttons.writing") }}
            </button>
          </div>
        </div>
        <div class="relative">
          <div class="border border-t-0 rounded-br rounded-bl">
            <editor-content
              :editor="editorInstance"
              class="story-editor w-full h-full"
            />
          </div>
          <div
            v-show="aiWritingPopupShow"
            class="absolute top-0 left-0 right-0 bottom-0"
          ></div>
        </div>
      </div>
      <ai-writing-popup
        v-if="currentStory"
        ref="aiWritingRef"
        v-model:show="aiWritingPopupShow"
        :editor="editorInstance"
        :mode="aiWritingMode"
        :story-id="currentStory.id!"
      />
      <popup
        v-model:show="showMomentSelector"
        class="h-[80%] overflow-hidden"
        position="bottom"
      >
        <div class="grid grid-rows-[auto_1fr] overflow-hidden h-full">
          <div class="navbar bg-base-100 h-top-nav border-b">
            <!-- 左 -->
            <div class="navbar-start h-full">
              <button class="btn btn-ghost" @click="showMomentSelector = false">
                <light-arrow-icon />
              </button>
            </div>
            <div class="navbar-center">
              <div>{{ $t("Message.selectMoment") }}</div>
            </div>
            <div class="navbar-end">
              <button
                :disabled="Object.keys(selectedMoments).length === 0"
                class="btn btn-primary btn-sm rounded"
                @click="onWriteByMoment"
              >
                {{ $t("Buttons.confirm") }}
              </button>
            </div>
          </div>
          <moment-selection
            ref="momentSelectorRef"
            v-model:selected-moments="selectedMoments"
            :max-count="1"
          />
        </div>
      </popup>
    </template>
  </global-scroll-layout>
</template>
<style>
.tiptap {
  @apply w-full h-full outline-0 focus:outline-none;
}

.story-editor .tiptap {
  @apply min-h-[700px] px-4 py-1;
}

.tiptap p.is-editor-empty:first-child::before {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.tiptap p[indent="true"] {
  @apply indent-[2rem];
}
</style>
